import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { AlertCircle, Check, Loader2, RefreshCw } from 'lucide-react';
import AssetImageField from '../AssetImageField';
import ShareLinkCard from './ShareLinkCard';
import CustomStepsEditor from './CustomStepsEditor';
import {
  WELCOME_MAX,
  NAME_MAX,
  customStepsError,
  initialsFrom,
  inviteCodeError,
  normalizeInviteCodeInput,
  photoUrlError,
  presetName,
  priceWording,
  settingsPatch,
  settingsToForm,
  trialWording,
} from '../../utils/dietitianInvite';
import { btnPrimary, btnSecondary, card, errorText, hint, input, inputError, label, sectionTitle } from './ui';

function PlanPicker({ presets, value, onChange, t, language, name }) {
  if (!presets.length) {
    return <p className="text-[13px] text-ink-muted">{t('form.noPlans')}</p>;
  }
  const options = [{ id: null }, ...presets];
  return (
    <div role="radiogroup" aria-label={t('form.planSection')} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {options.map((preset) => {
        const selected = (value || null) === preset.id;
        const isDefault = preset.id === null;
        return (
          <label
            key={preset.id || '__company'}
            className={`flex cursor-pointer gap-3 rounded-lg border p-3 transition duration-120 focus-within:ring-2 focus-within:ring-forest ${
              selected ? 'border-forest bg-forest-faint' : 'border-line bg-surface hover:bg-moss'
            }`}
          >
            <input
              type="radio"
              name={name}
              className="mt-0.5 h-4 w-4 accent-[color:var(--forest-ondark)]"
              checked={selected}
              onChange={() => onChange(preset.id)}
            />
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold text-ink">
                {isDefault ? t('form.planCompany') : presetName(preset, language)}
              </span>
              {isDefault ? (
                <span className="block text-[11px] text-ink-muted mt-0.5">{t('form.planCompanyHint')}</span>
              ) : (
                <>
                  <span className="block text-[13px] text-ink tabular-nums mt-0.5" dir="ltr">{priceWording(preset, 'english')}</span>
                  <span className="block text-[11px] text-ink-muted mt-0.5">{trialWording(preset, language)}</span>
                </>
              )}
            </span>
          </label>
        );
      })}
    </div>
  );
}

function PreviewCard({ form, profileName, companyName, preset, t, language }) {
  const name = form.display_name.trim() || profileName || '';
  const photoOk = form.photo_url && !photoUrlError(form.photo_url);
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [form.photo_url]);

  return (
    <section className={`${card} p-4`} aria-label={t('preview.title')}>
      <h2 className={sectionTitle}>{t('preview.title')}</h2>
      <p className={`${hint} mt-0.5`}>{t('preview.hint')}</p>
      <div className="mt-4 rounded-lg border border-line bg-canvas p-4 text-center">
        <div className="mx-auto h-20 w-20 overflow-hidden rounded-full border border-line bg-forest-faint flex items-center justify-center">
          {photoOk && !broken ? (
            <img src={form.photo_url} alt="" className="h-full w-full object-cover" onError={() => setBroken(true)} />
          ) : (
            <span className="text-[24px] font-semibold text-forest-ondark">{initialsFrom(name) || '?'}</span>
          )}
        </div>
        <p className="mt-3 text-[18px] font-semibold text-ink">{name || '—'}</p>
        {form.title.trim() ? <p className="text-[13px] text-ink-muted">{form.title.trim()}</p> : null}
        {companyName ? <p className="mt-1 text-[11px] font-medium uppercase tracking-wide text-ink-muted">{companyName}</p> : null}
        {form.welcome_message.trim() ? (
          <p className="mt-3 whitespace-pre-line text-[13px] text-ink text-start">{form.welcome_message.trim()}</p>
        ) : null}
        {preset ? (
          <p className="mt-3 text-[11px] text-ink-muted">
            <span dir="ltr" className="tabular-nums">{priceWording(preset, 'english')}</span> · {trialWording(preset, language)}
          </p>
        ) : null}
        <div aria-hidden className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center rounded-md bg-forest-ondark text-[13px] font-semibold text-white dark:text-canvas-dark">
          {t('preview.join')}
        </div>
      </div>
    </section>
  );
}

/**
 * The coach's invite settings: code, landing info, plan and extra questions, with
 * the share card and a live preview. Used by My link (own settings) and the Team
 * side panel (a manager editing a coach). `load` / `save` pick the endpoint.
 */
export default function DietitianSettingsForm({
  load,
  save,
  t,
  language,
  companyName,
  canUpload = false,
  layout = 'page',
  idPrefix = 'dset',
  onSaved,
}) {
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [loadError, setLoadError] = useState('');
  const [original, setOriginal] = useState(null);
  const [presets, setPresets] = useState([]);
  const [suggested, setSuggested] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [codeTaken, setCodeTaken] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  const apply = useCallback((data) => {
    setOriginal(data.settings || {});
    setPresets(Array.isArray(data.presets) ? data.presets : []);
    setSuggested(data.suggested_code || null);
    setForm(settingsToForm(data.settings, data.suggested_code));
  }, []);

  const fetchSettings = useCallback(async () => {
    setStatus('loading');
    setLoadError('');
    try {
      apply(await load());
      setStatus('ready');
    } catch (err) {
      setLoadError(err.message || '');
      setStatus('error');
    }
  }, [load, apply]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const errors = useMemo(() => {
    if (!form) return {};
    return {
      code: inviteCodeError(form.invite_code),
      photo: photoUrlError(form.photo_url),
      steps: customStepsError(form.custom_steps),
    };
  }, [form]);

  const patch = useMemo(() => (form && original ? settingsPatch(original, form) : {}), [form, original]);
  const dirty = Object.keys(patch).length > 0;
  const selectedPreset = presets.find((p) => p.id === form?.pricing_preset_id) || null;

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (key === 'invite_code') setCodeTaken(false);
    setSaveError('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (errors.code || errors.photo || errors.steps) {
      setShowErrors(true);
      setSaveError(t('form.fixErrors'));
      return;
    }
    if (!dirty) {
      toast(t('form.nothingToSave'));
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const data = await save(patch);
      apply(data);
      setShowErrors(false);
      toast.success(t('form.saved'));
      if (onSaved) onSaved(data);
    } catch (err) {
      if (err.status === 409) {
        setCodeTaken(true);
        setSaveError(t('form.code.taken'));
      } else {
        setSaveError(t('form.saveError', { msg: err.message || '' }));
      }
    } finally {
      setSaving(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="flex items-center gap-2 py-12 justify-center text-[13px] text-ink-muted" role="status">
        <Loader2 size={18} className="animate-spin motion-reduce:animate-none" aria-hidden />
        {t('loading')}
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={`${card} p-6 text-center`} role="alert">
        <AlertCircle size={24} className="mx-auto text-[color:var(--danger-text)]" aria-hidden />
        <p className="mt-2 text-[13px] text-ink">{t('loadError')}</p>
        {loadError ? <p className={`${hint} mt-1`}>{loadError}</p> : null}
        <button type="button" className={`${btnPrimary} mt-4`} onClick={fetchSettings}>
          <RefreshCw size={16} aria-hidden />
          {t('retry')}
        </button>
      </div>
    );
  }

  const codeErrorKey = codeTaken ? 'form.code.taken' : showErrors && errors.code ? `form.code.${errors.code}` : null;
  const isPanel = layout === 'panel';
  const usingSuggestion = !original?.invite_code && suggested && form.invite_code === suggested;

  const fields = (
    <form onSubmit={onSubmit} noValidate className="space-y-6">
      <p className="text-[13px] text-ink-muted">{t('form.inherit')}</p>

      {/* Code */}
      <section className={`${card} p-4 space-y-2`}>
        <h2 className={sectionTitle}>{t('form.codeSection')}</h2>
        <label htmlFor={`${idPrefix}-code`} className={label}>{t('form.code')}</label>
        <input
          id={`${idPrefix}-code`}
          className={`${input} font-mono text-[15px] uppercase tracking-wider ${codeErrorKey ? inputError : ''}`}
          dir="ltr"
          value={form.invite_code}
          onChange={(e) => set('invite_code', normalizeInviteCodeInput(e.target.value))}
          autoComplete="off"
          spellCheck={false}
          aria-invalid={codeErrorKey ? 'true' : undefined}
          aria-describedby={`${idPrefix}-code-hint`}
        />
        {codeErrorKey ? <p className={errorText} role="alert">{t(codeErrorKey)}</p> : null}
        <p id={`${idPrefix}-code-hint`} className={hint}>
          {usingSuggestion ? t('form.codeSuggested') : t('form.codeHint')}
        </p>
      </section>

      {/* Profile */}
      <section className={`${card} p-4 space-y-4`}>
        <h2 className={sectionTitle}>{t('form.profileSection')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={`${idPrefix}-name`} className={label}>{t('form.displayName')}</label>
            <input id={`${idPrefix}-name`} className={input} maxLength={NAME_MAX} value={form.display_name}
              placeholder={original?.profile_name || t('form.displayNamePlaceholder')}
              onChange={(e) => set('display_name', e.target.value)} />
          </div>
          <div>
            <label htmlFor={`${idPrefix}-title`} className={label}>{t('form.title')}</label>
            <input id={`${idPrefix}-title`} className={input} maxLength={NAME_MAX} value={form.title}
              placeholder={t('form.titlePlaceholder')} onChange={(e) => set('title', e.target.value)} />
          </div>
        </div>

        <div className="space-y-2">
          {canUpload ? (
            <AssetImageField value={form.photo_url} onChange={(url) => set('photo_url', url || '')} folder="coaches" label={t('form.photo')} />
          ) : null}
          <label htmlFor={`${idPrefix}-photo`} className={label}>{t('form.photoUrl')}</label>
          <input id={`${idPrefix}-photo`} type="url" dir="ltr" className={`${input} ${showErrors && errors.photo ? inputError : ''}`}
            value={form.photo_url} placeholder="https://"
            aria-invalid={showErrors && errors.photo ? 'true' : undefined}
            onChange={(e) => set('photo_url', e.target.value)} />
          {(showErrors || form.photo_url.length > 8) && errors.photo ? <p className={errorText}>{t('form.photo.https')}</p> : null}
        </div>

        <div>
          <label htmlFor={`${idPrefix}-welcome`} className={label}>{t('form.welcome')}</label>
          <textarea id={`${idPrefix}-welcome`} className={`${input} min-h-[96px] py-2 resize-y`} maxLength={WELCOME_MAX}
            value={form.welcome_message} placeholder={t('form.welcomePlaceholder')}
            aria-describedby={`${idPrefix}-welcome-count`}
            onChange={(e) => set('welcome_message', e.target.value)} />
          <p id={`${idPrefix}-welcome-count`} className={`${hint} text-end tabular-nums`}>{form.welcome_message.length}/{WELCOME_MAX}</p>
        </div>
      </section>

      {/* Plan */}
      <section className={`${card} p-4 space-y-3`}>
        <div>
          <h2 className={sectionTitle}>{t('form.planSection')}</h2>
          <p className={`${hint} mt-0.5`}>{t('form.planHint')}</p>
        </div>
        <PlanPicker presets={presets} value={form.pricing_preset_id} onChange={(id) => set('pricing_preset_id', id)}
          t={t} language={language} name={`${idPrefix}-plan`} />
      </section>

      {/* Extra questions */}
      <section className={`${card} p-4 space-y-3`}>
        <div>
          <h2 className={sectionTitle}>{t('form.questionsSection')}</h2>
          <p className={`${hint} mt-0.5`}>{t('form.questionsHint')}</p>
        </div>
        <CustomStepsEditor steps={form.custom_steps} onChange={(steps) => set('custom_steps', steps)} t={t}
          error={showErrors ? errors.steps : null} idPrefix={`${idPrefix}-step`} />
      </section>

      {/* Save */}
      <div className="sticky bottom-0 z-10 -mx-4 border-t border-line bg-canvas px-4 py-3 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:p-0">
        {saveError ? <p className={`${errorText} mb-2 text-[13px]`} role="alert">{saveError}</p> : null}
        <div className="flex flex-wrap gap-2">
          <button type="submit" className={`${btnPrimary} flex-1 sm:flex-none`} disabled={saving}>
            {saving ? <Loader2 size={16} className="animate-spin motion-reduce:animate-none" aria-hidden /> : <Check size={16} aria-hidden />}
            {saving ? t('form.saving') : t('form.save')}
          </button>
          {dirty && !saving ? (
            <button type="button" className={btnSecondary} onClick={() => { setForm(settingsToForm(original, suggested)); setShowErrors(false); setCodeTaken(false); setSaveError(''); }}>
              {t('form.reset')}
            </button>
          ) : null}
        </div>
      </div>
    </form>
  );

  const share = (
    <ShareLinkCard code={original?.invite_code} link={original?.client_link} t={t} language={language} compact={isPanel} dirty={dirty} />
  );
  const preview = (
    <PreviewCard form={form} profileName={original?.profile_name} companyName={companyName} preset={selectedPreset} t={t} language={language} />
  );

  if (isPanel) {
    return (
      <div className="space-y-6">
        {share}
        {preview}
        {fields}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {share}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-6 items-start">
        {fields}
        <div className="lg:sticky lg:top-24">{preview}</div>
      </div>
    </div>
  );
}
